// Nine Lives '84 - the fishbowl room.
//
// A bowl on a table across the room. Touch it and the screen becomes the
// inside of the tank: swim (with the water's inertia) and eat every fish.
// Electric eels cruise the lanes; a shock stuns and throws Tabs. Air
// runs out underwater; come up to the surface to breathe, or drown. At the
// surface the action button climbs out, back into the room, keeping the
// fish already eaten.

import type { Cat } from '../cat';
import {
  AIR_REFILL,
  DT,
  FREEZE_SHORT,
  EEL_H,
  EEL_W,
  FISH_H,
  FISH_W,
  K_ACTION,
  K_DOWN,
  K_LEFT,
  K_RIGHT,
  K_UP,
  SHOCK_TICKS,
  SWIM_ACCEL,
  SWIM_DRAG,
  SWIM_MAX,
  SWIM_SINK,
  WATER_SURFACE,
  WORLD_W,
} from '../constants';
import { overlaps } from '../physics';
import { Room, type RoomContext } from '../room';
import type { Box, GameEvent, Platform, RoomKind } from '../types';

export interface Fish extends Box {
  vx: number;
  /** Phase of the wiggle. */
  t: number;
  baseY: number;
}

export interface Eel extends Box {
  vx: number;
  t: number;
  baseY: number;
}

export type FishbowlPhase = 'room' | 'tank';

export class FishbowlRoom extends Room {
  readonly kind: RoomKind = 'fishbowl';
  readonly taskName = 'fish';
  readonly table: Platform = { kind: 'furniture', x: 196, y: 27, w: 100, h: 3, oneWay: true };
  /** The bowl on the table; touching it is the dive. */
  readonly bowl: Box = { x: 208, y: 30, w: 76, h: 58 };
  phase: FishbowlPhase = 'room';
  fish: Fish[] = [];
  eels: Eel[] = [];
  eaten = 0;
  air: number;
  /** Ticks of eel stun left. */
  stun = 0;
  private atSurface = false;

  constructor(ctx: RoomContext) {
    super(ctx);
    this.platforms.push(this.table);
    this.air = ctx.params.airTicks;
    const { rng, params } = ctx;
    for (let i = 0; i < params.fishCount; i++) {
      const y = rng.int(30, WATER_SURFACE - 40);
      this.fish.push({
        x: rng.int(20, WORLD_W - 40),
        y,
        w: FISH_W,
        h: FISH_H,
        vx: (rng.chance(0.5) ? 1 : -1) * params.fishSpeed * (0.7 + rng.next() * 0.6),
        t: rng.next() * 6,
        baseY: y,
      });
    }
    for (let i = 0; i < params.eelCount; i++) {
      const y = 40 + i * 45;
      this.eels.push({
        x: rng.int(0, WORLD_W - EEL_W),
        y,
        w: EEL_W,
        h: EEL_H,
        vx: (i % 2 ? -1 : 1) * params.eelSpeed,
        t: rng.next() * 6,
        baseY: y,
      });
    }
  }

  get need(): number {
    return this.ctx.params.fishCount;
  }

  get have(): number {
    return this.eaten;
  }

  get airFraction(): number {
    return this.air / this.ctx.params.airTicks;
  }

  protected override catBusy(): boolean {
    return this.phase === 'tank';
  }

  protected override get broomActive(): boolean {
    return this.phase === 'room';
  }

  /** Tabs' head is out of the water. */
  get surfaced(): boolean {
    return this.phase === 'tank' && this.cat_y_top >= WATER_SURFACE - 1;
  }

  private cat_y_top = 0;

  protected update(cat: Cat, keys: number, events: GameEvent[]): void {
    if (this.phase === 'room') {
      if (cat.knockedTicks === 0 && overlaps(cat, this.bowl)) this.dive(cat, events);
      return;
    }
    this.swim(cat, keys);
    this.cat_y_top = cat.y + cat.h;
    this.updateCreatures(cat, events);
    this.updateAir(cat, keys, events);
  }

  private dive(cat: Cat, events: GameEvent[]): void {
    this.phase = 'tank';
    cat.x = WORLD_W / 2 - cat.w / 2;
    cat.y = WATER_SURFACE - 30;
    cat.vx = 0;
    cat.vy = 0;
    cat.onGround = false;
    cat.ground = null;
    cat.leaping = false;
    cat.knockedTicks = 0;
    this.air = this.ctx.params.airTicks;
    this.stun = 0;
    this.atSurface = false;
    this.freezeRequest = FREEZE_SHORT;
    events.push({ kind: 'dive' });
  }

  private climbOut(cat: Cat, events: GameEvent[]): void {
    this.phase = 'room';
    cat.x = this.table.x + 4;
    cat.y = this.table.y + this.table.h;
    cat.vx = 0;
    cat.vy = 0;
    cat.onGround = true;
    cat.ground = this.table;
    cat.facing = -1;
    this.freezeRequest = FREEZE_SHORT;
    events.push({ kind: 'climbOut' });
  }

  private swim(cat: Cat, keys: number): void {
    if (this.stun > 0) {
      this.stun--;
    } else {
      const dx = keys & K_RIGHT ? 1 : keys & K_LEFT ? -1 : 0;
      const dy = keys & K_UP ? 1 : keys & K_DOWN ? -1 : 0;
      if (dx) cat.facing = dx;
      cat.vx += dx * SWIM_ACCEL * DT;
      cat.vy += dy * SWIM_ACCEL * DT;
    }
    cat.vy -= SWIM_SINK * DT;
    cat.vx *= SWIM_DRAG;
    cat.vy *= SWIM_DRAG;
    const speed = Math.hypot(cat.vx, cat.vy);
    if (speed > SWIM_MAX) {
      cat.vx *= SWIM_MAX / speed;
      cat.vy *= SWIM_MAX / speed;
    }
    cat.x += cat.vx * DT;
    cat.y += cat.vy * DT;
    if (cat.x < 0) {
      cat.x = 0;
      cat.vx = 0;
    } else if (cat.x + cat.w > WORLD_W) {
      cat.x = WORLD_W - cat.w;
      cat.vx = 0;
    }
    const top = WATER_SURFACE - cat.h + 4; // head can break the surface
    if (cat.y < 2) {
      cat.y = 2;
      cat.vy = 0;
    } else if (cat.y > top) {
      cat.y = top;
      cat.vy = 0;
    }
    cat.onGround = false;
    cat.ground = null;
    if (cat.knockedTicks > 0) cat.knockedTicks--;
  }

  private updateCreatures(cat: Cat, events: GameEvent[]): void {
    const { rng } = this.ctx;
    this.fish = this.fish.filter((f) => {
      f.t += DT;
      f.x += f.vx * DT;
      f.y = f.baseY + Math.sin(f.t * 2.5) * 8;
      if (f.x < 0 || f.x + f.w > WORLD_W) {
        f.vx = -f.vx;
        f.x = Math.max(0, Math.min(WORLD_W - f.w, f.x));
      }
      if (rng.chance(0.004)) f.vx = -f.vx;
      if (this.stun === 0 && overlaps(cat, f)) {
        this.eaten++;
        events.push({ kind: 'fishEaten', have: this.eaten, need: this.need });
        return false;
      }
      return true;
    });
    if (this.eaten >= this.need) {
      this.done = true;
      return;
    }
    for (const e of this.eels) {
      e.t += DT;
      e.x += e.vx * DT;
      e.y = e.baseY + Math.sin(e.t * 1.8) * 12;
      if (e.x < 0 || e.x + e.w > WORLD_W) {
        e.vx = -e.vx;
        e.x = Math.max(0, Math.min(WORLD_W - e.w, e.x));
      }
      if (this.stun === 0 && overlaps(cat, e)) {
        this.stun = SHOCK_TICKS;
        const dir = cat.x + cat.w / 2 < e.x + e.w / 2 ? -1 : 1;
        cat.vx = dir * 110;
        cat.vy = 50;
        cat.knockedTicks = SHOCK_TICKS;
        events.push({ kind: 'knocked', by: 'eel' });
        events.push({ kind: 'shock' });
      }
    }
  }

  private updateAir(cat: Cat, keys: number, events: GameEvent[]): void {
    if (this.surfaced) {
      if (!this.atSurface) {
        this.atSurface = true;
        events.push({ kind: 'surface' });
      }
      this.air = Math.min(this.ctx.params.airTicks, this.air + AIR_REFILL);
      if (keys & K_ACTION && this.stun === 0) this.climbOut(cat, events);
      return;
    }
    this.atSurface = false;
    if (--this.air <= 0) {
      this.air = 0;
      this.fatal = 'drowned';
    }
  }
}
